import { environmentHttpClient } from "client/Api/httpClient";
import { HubItemCategoryNodeKind } from "./common";
import { HubDataSource, HubItemDataSource } from "./hub";
import { CommunityKind } from "../Communities/community";
import { HubItemKind } from "./hubItemCategories";

export type CategoryHubSidebarNodeViewModel = {
	kind: HubItemCategoryNodeKind.Category;
	categoryId: number;
	name: string;
};

export type GroupHubSidebarNodeViewModel = {
	kind: HubItemCategoryNodeKind.Group;
	groupId: number;
	name: string;
	children: readonly HubItemHubSideBarNodeViewModel[];
};

export type HubItemHubSideBarNodeViewModel =
	| GroupHubSidebarNodeViewModel
	| CategoryHubSidebarNodeViewModel;

export type HubItemRootHubSideBarNodeViewModel = {
	source: HubItemDataSource;
	children: readonly HubItemHubSideBarNodeViewModel[];
};

export type CommunityHubSidebarViewModelPermissions = {
	canEdit: boolean;
	canDelete: boolean;
};

export type CommunityHubSidebarViewModel = {
	id: number;
	name: string;
	kind: CommunityKind;
	permissions: CommunityHubSidebarViewModelPermissions;
};

export type CommunityRootHubSidebarViewModel = {
	source: HubDataSource.Community;
	children: readonly CommunityHubSidebarViewModel[];
};

export type RootHubSideBarNodeViewModel =
	| HubItemRootHubSideBarNodeViewModel
	| CommunityRootHubSidebarViewModel;

export type HubHierarchyViewModel = {
	shouldUseDefaultSorting: boolean;
	nodes: readonly RootHubSideBarNodeViewModel[];
};

export type CategoryHubSidebarNodeDataSaveModel = {
	categoryId: number;
};

export type CategoryHubItemSidebarNodeSaveModel = {
	kind: HubItemCategoryNodeKind.Category;
	categoryData: CategoryHubSidebarNodeDataSaveModel;
};

export type GroupHubSidebarNodeDataSaveModel = {
	groupId: number;
	children: readonly HubItemSidebarNodeSaveModel[];
};

export type GroupHubItemSidebarNodeSaveModel = {
	kind: HubItemCategoryNodeKind.Group;
	groupData: GroupHubSidebarNodeDataSaveModel;
};

export type HubItemSidebarNodeSaveModel =
	| GroupHubItemSidebarNodeSaveModel
	| CategoryHubItemSidebarNodeSaveModel;

export type BaseHubItemRootHubSidebarNodeSaveModel = {
	children: readonly HubItemSidebarNodeSaveModel[];
};

export type AnnouncementRootHubSidebarNodeSaveModel = BaseHubItemRootHubSidebarNodeSaveModel & {
	source: HubDataSource.Announcement;
};

export type EventRootHubSidebarNodeSaveModel = BaseHubItemRootHubSidebarNodeSaveModel & {
	source: HubDataSource.Event;
};

export type KnowledgeItemRootHubSidebarNodeSaveModel = BaseHubItemRootHubSidebarNodeSaveModel & {
	source: HubDataSource.KnowledgeItem;
};

export type CommunitiesRootHubSidebarNodeSaveModel = {
	source: HubDataSource.Community;
};

export enum HubHierarchySortKind {
	Default = 1,
	Custom = 2
}

export type HubHierarchySortingSaveModel = {
	kind: HubHierarchySortKind;
	sourcesInOrder: readonly HubDataSource[] | null;
};

export type HubHierarchySaveModel = {
	sorting: OptionalModel<HubHierarchySortingSaveModel>;
	announcementsRoot: OptionalModel<AnnouncementRootHubSidebarNodeSaveModel>;
	communitiesRoot: OptionalModel<CommunitiesRootHubSidebarNodeSaveModel>;
	eventsRoot: OptionalModel<EventRootHubSidebarNodeSaveModel>;
	knowledgeItemsRoot: OptionalModel<KnowledgeItemRootHubSidebarNodeSaveModel>;
};

export type HubHierarchyManagementView = {
	canUserReorderRootNodesAndChangeSortKind: boolean;
	hierarchy: HubHierarchyViewModel;
};

export function getHubSidebarManagement(
	signal?: AbortSignal
): Promise<HubHierarchyManagementView> {
	return environmentHttpClient.get<HubHierarchyManagementView>(
		"/UiApi/HubSidebar/Hierarchy/Edit",
		undefined,
		{ signal }
	);
}

export function saveHubSidebar(
	model: HubHierarchySaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		"/UiApi/HubSidebar/Hierarchy",
		model,
		{ signal }
	);
}

export type KnowledgeItemGroupInfoSaveModel = {
	description: string | null;
	image: OptionalModel<Base64FileUriUploadModel | null>;
};

export type HubItemGroupSaveModel = {
	name: string;
	knowledgeItemInformation: OptionalModel<KnowledgeItemGroupInfoSaveModel>;
};

export type CreateHubItemGroupSaveModel = HubItemGroupSaveModel & {
	itemKind: HubItemKind;
};

export function createHubItemGroup(
	model: CreateHubItemGroupSaveModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"/UiApi/HubSidebar/Hierarchy/Groups",
		model,
		{ signal }
	);
}

export function updateHubItemGroup(
	id: number,
	model: HubItemGroupSaveModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.put<number>(
		`/UiApi/HubSidebar/Hierarchy/Groups/${id}`,
		model,
		{ signal }
	);
}

export type KnowledgeItemGroupInfoModel = {
	description: string | null;
	imageId: string | null;
};

export type HubItemGroupModel = {
	name: string;
	itemKind: HubItemKind;
	knowledgeItemInformation: OptionalModel<KnowledgeItemGroupInfoModel>;
};

export type HubItemGroupImageRestrictions = {
	supportedImageTypes: readonly string[];
	maxImageSize: number;
};

export type CreateHubItemGroupModel = {
	imageRestrictions: HubItemGroupImageRestrictions;
};

export type UpdateHubItemGroupModel = {
	group: HubItemGroupModel;
	imageRestrictions: HubItemGroupImageRestrictions;
};

export function getCreateHubItemGroup(
	signal?: AbortSignal
): Promise<CreateHubItemGroupModel> {
	return environmentHttpClient.get<CreateHubItemGroupModel>(
		`/UiApi/HubSidebar/Hierarchy/Groups/New`,
		undefined,
		{ signal }
	);
}

export function getUpdateHubItemGroup(
	id: number,
	signal?: AbortSignal
): Promise<UpdateHubItemGroupModel> {
	return environmentHttpClient.get<UpdateHubItemGroupModel>(
		`/UiApi/HubSidebar/Hierarchy/Groups/${id}`,
		undefined,
		{ signal }
	);
}